import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import RoleBasedRedirect from './components/auth/RoleBasedRedirect';

// Landing Page
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import RegistrationPending from './pages/auth/RegistrationPending';
import OtpVerification from './pages/auth/OtpVerification';

// Dashboard Pages
import ClientDashboard from './pages/dashboard/ClientDashboard';
import DeveloperDashboard from './pages/dashboard/DeveloperDashboard';
import AdminDashboard from './pages/dashboard/AdminDashboard';

// Project Pages
import ProjectsList from './pages/projects/ProjectsList';
import ProjectDetails from './pages/projects/ProjectDetails';
import CreateProject from './pages/projects/CreateProject';

// Task Pages
import TasksList from './pages/tasks/TasksList';
import TaskDetails from './pages/tasks/TaskDetails';
import CreateTask from './pages/tasks/CreateTask';
import TaskAssignment from './pages/tasks/TaskAssignment';
import DeadlineExtensionRequest from './pages/tasks/DeadlineExtensionRequest';

// Communication Pages
import ChatInterface from './pages/communication/ChatInterface';

// Payment Pages
import PaymentCenter from './pages/payments/PaymentCenter';
import PaymentSuccess from './pages/payments/PaymentSuccess';
import PaymentRequestDetails from './pages/payments/PaymentRequestDetails';

// Admin Payment Pages
import CreatePaymentRequest from './pages/admin/CreatePaymentRequest';
import PaymentRequests from './pages/admin/PaymentRequests';
import AdminPaymentRequestDetails from './pages/admin/PaymentRequestDetails';

// Earnings Pages
import EarningsOverview from './pages/earnings/EarningsOverview';
import WithdrawalRequests from './pages/earnings/WithdrawalRequests';

// Profile Pages
import UserProfile from './pages/profile/UserProfile';
import Settings from './pages/profile/Settings';

// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import SystemSettings from './pages/admin/SystemSettings';
import DeveloperApprovals from './pages/admin/DeveloperApprovals';
import ActivityLog from './pages/admin/ActivityLog';
import VisitorAnalytics from './pages/admin/VisitorAnalytics';

// Shared Components
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import NotificationCenter from './components/notifications/NotificationCenter';
import EditProject from './pages/EditProject';
import PaymentDetails from './pages/PaymentDetails';
import Payments from './pages/Payments';
import ManagePaymentMethodsPage from './pages/dashboard/payments/ManagePaymentMethodsPage';
import AdminPaymentHistory from './pages/dashboard/payments/AdminPaymentHistory';
import NotificationsPage from './pages/notifications/NotificationsPage';
import PaymentHistory from './pages/dashboard/payments/PaymentHistory';

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <SocketProvider>
              <div className="App dark:bg-gray-900">
                <Routes>
                  {/* Landing Page - Default Route */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                  {/* Public Routes */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <PublicRoute>
                        <Register />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/registration-pending"
                    element={
                      <PublicRoute>
                        <RegistrationPending />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <PublicRoute>
                        <ResetPassword />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/verify-email"
                    element={
                      <PublicRoute>
                        <VerifyEmail />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path="/verify-otp"
                    element={
                      <PublicRoute>
                        <OtpVerification />
                      </PublicRoute>
                    }
                  />

                  {/* Protected Dashboard Routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Dashboard Home - Role-based redirect */}
                    <Route index element={<RoleBasedRedirect />} />
                    <Route
                      path="projects/:id/edit"
                      element={
                        <ProtectedRoute requiredRole={['client', 'admin']}>
                          <EditProject />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <ProtectedRoute>
                          <NotificationsPage /> {/* You’ll create this next */}
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="payments/history"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <PaymentHistory />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="payments/methods"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <ManagePaymentMethodsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Client-only routes */}
                    <Route
                      path="client"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <ClientDashboard />
                        </ProtectedRoute>
                      }
                    />
                
                    <Route
                      path="payments"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <PaymentCenter />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="payments/success"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <PaymentSuccess />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="payments/requests/:id"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <PaymentRequestDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Developer-only routes */}
                    <Route
                      path="developer"
                      element={
                        <ProtectedRoute requiredRole="developer">
                          <DeveloperDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="earnings"
                      element={
                        <ProtectedRoute requiredRole="developer">
                          <EarningsOverview />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="earnings/withdrawals"
                      element={
                        <ProtectedRoute requiredRole="developer">
                          <WithdrawalRequests />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin-only routes */}
                    <Route
                      path="admin"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/users"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/settings"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <SystemSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/analytics"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <VisitorAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/developer-approvals"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <DeveloperApprovals />
                        </ProtectedRoute>
                      }
                    />
                  
                    <Route
                      path="admin/payment-requests"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <PaymentRequests />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/payment-requests/create"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <CreatePaymentRequest />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/payment-requests/:id"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminPaymentRequestDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/payments"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminPaymentHistory />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="create-task"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <CreateTask />
                        </ProtectedRoute>
                      }
                    />

                    {/* Shared routes (accessible by multiple roles) */}
                    <Route path="projects" element={<ProjectsList />} />
                    <Route
                      path="projects/create"
                      element={
                        <ProtectedRoute requiredRole="client">
                          <CreateProject />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="projects/:id" element={<ProjectDetails />} />

                    <Route path="tasks" element={<TasksList />} />
                    <Route path="tasks/:id" element={<TaskDetails />} />
                    <Route
                      path="task-assignments/:id"
                      element={<TaskAssignment />}
                    />
                    <Route
                      path="extension-requests/:id"
                      element={<DeadlineExtensionRequest />}
                    />

                    <Route path="chat" element={<ChatInterface />} />
                    <Route
                      path="chat/:conversationId"
                      element={<ChatInterface />}
                    />

                    <Route path="profile" element={<UserProfile />} />
                    <Route path="settings" element={<Settings />} />
                  </Route>

                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </SocketProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
